import { api } from "./client";

export interface FleetUnitKpi {
  readings: number;
  lprEvents: number;
  hits: number;
  hitsReportados: number;
  hitsDescartados: number;
  hitsAutoridadNotificada: number;
  pausasCount: number;
  pausasSeconds: number;
  sesionesCount: number;
  distanciaKm: number;
  tiempoMovimientoSeconds: number;
  tiempoDetenidoSeconds: number;
  anomaliasAbiertas: number;
}

export interface FleetUnitSession {
  sesionId: number;
  inicio: string;
  minutosActiva: number;
}

export interface FleetUnitPause {
  pausaId: number;
  motivoId: number;
  motivoNombre: string | null;
  inicio: string;
  minutosPausa: number;
}

export interface FleetUnitLastHit {
  hitId: number;
  fase: string;
  vin: string | null;
  placa: string | null;
  at: string;
}

export interface FleetUnit {
  unitId: number;
  alias: string;
  placa: string | null;
  gerencia: string | null;
  estado: string;
  latitude: number | null;
  longitude: number | null;
  heading: number | null;
  speedKmh: number | null;
  ignicion: boolean | null;
  operativoId: number | null;
  operativoNombre: string | null;
  usuarioId: number | null;
  operadorNombre: string | null;
  lastHeartbeatAt: string | null;
  lastReadingAt: string | null;
  // v2 (PR 4) — daily rollups + per-unit session/pause/last-hit context.
  // Older endpoints may omit them; treat as optional during the
  // transition window.
  kpiToday?: FleetUnitKpi;
  sesionActiva?: FleetUnitSession | null;
  pausaActiva?: FleetUnitPause | null;
  lastHit?: FleetUnitLastHit | null;
}

export interface FleetSnapshotTotals {
  totalUnits: number;
  monitoreando: number;
  detenido: number;
  enPausa: number;
  offline: number;
  hitsToday: number;
  distanciaKmToday: number;
  anomaliasAbiertas: number;
}

export interface FleetSnapshot {
  serverTime: string;
  cachedAt?: string | null;
  cacheTtlSeconds?: number;
  totals?: FleetSnapshotTotals;
  units: FleetUnit[];
}

export async function getFleetSnapshot(): Promise<FleetSnapshot> {
  const res = await api.get<FleetSnapshot>("/fleet/snapshot");
  return res.data;
}

export interface UnitTimeline {
  unitId: number;
  alias: string;
  estado: string;
  recentReadings: Array<{
    id: number;
    epc: string;
    vin: string | null;
    rssi: number | null;
    antena: number;
    at: string;
    latitude: number | null;
    longitude: number | null;
  }>;
  recentHits: Array<{
    id: number;
    vin: string | null;
    placa: string | null;
    fase: string;
    at: string;
  }>;
  recentTelemetry: Array<{
    at: string;
    cpuPct: number | null;
    ramPct: number | null;
    discoPct: number | null;
    cameraLprStatus: string | null;
  }>;
}

export async function getUnitTimeline(unitId: number): Promise<UnitTimeline> {
  const res = await api.get<UnitTimeline>(`/fleet/units/${unitId}/timeline`);
  return res.data;
}

// ---------------------------------------------------------------------------
// /fleet/units/{id}/detail (PR 5)
// ---------------------------------------------------------------------------

export interface UnitInfo {
  unitId: number;
  alias: string;
  placa: string | null;
  gerencia: string | null;
  tipoVehiculoId: number | null;
  vin: string | null;
  activo: boolean;
}

export interface UnitStateNow {
  estado: string;
  latitude: number | null;
  longitude: number | null;
  heading: number | null;
  speedKmh: number | null;
  ignicion: boolean | null;
  operativoId: number | null;
  operativoNombre: string | null;
  usuarioId: number | null;
  operadorNombre: string | null;
  lastHeartbeatAt: string | null;
  lastReadingAt: string | null;
}

export interface UnitKpiWindow {
  startDay: string;
  endDay: string;
  readings: number;
  lprEvents: number;
  hits: number;
  hitsReportados: number;
  hitsDescartados: number;
  hitsAutoridadNotificada: number;
  pausasCount: number;
  pausasSeconds: number;
  sesionesCount: number;
  distanciaKm: number;
  tiempoMovimientoSeconds: number;
  tiempoDetenidoSeconds: number;
  anomaliasAbiertas: number;
}

export interface UnitAlertSummary {
  severidad: string;
  abiertas: number;
  total24h: number;
}

export interface UnitLastTelemetry {
  at: string;
  cpuPct: number | null;
  ramPct: number | null;
  discoPct: number | null;
  temperaturaC: number | null;
  bateriaPct: number | null;
  cameraLprStatus: string | null;
  gpsFix: string | null;
  redTipo: string | null;
  redSignal: number | null;
}

export interface UnitDetail {
  info: UnitInfo;
  stateNow: UnitStateNow;
  kpiToday: UnitKpiWindow;
  kpiLast7d: UnitKpiWindow;
  kpiLast30d: UnitKpiWindow;
  sesionActiva: FleetUnitSession | null;
  pausaActiva: FleetUnitPause | null;
  lastHit: FleetUnitLastHit | null;
  lastTelemetry: UnitLastTelemetry | null;
  alerts: UnitAlertSummary[];
}

export async function getUnitDetail(unitId: number): Promise<UnitDetail> {
  const res = await api.get<UnitDetail>(`/fleet/units/${unitId}/detail`);
  return res.data;
}

// ---------------------------------------------------------------------------
// /fleet/units/{id}/sessions (PR 5)
// ---------------------------------------------------------------------------

export interface UnitSessionRow {
  sesionId: number;
  inicio: string;
  fin: string | null;
  duracionMinutos: number | null;
  motivoId: number;
  motivoNombre: string | null;
  comentarios: string | null;
  usuarioId: number;
  operadorNombre: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface UnitSessionsResponse {
  unitId: number;
  items: UnitSessionRow[];
  nextCursor: number | null;
}

export async function listUnitSessions(
  unitId: number,
  params: {
    start?: string;
    end?: string;
    limit?: number;
    cursor?: number;
  } = {}
): Promise<UnitSessionsResponse> {
  const res = await api.get<UnitSessionsResponse>(
    `/fleet/units/${unitId}/sessions`,
    { params }
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// /fleet/units/{id}/alerts (PR 5)
// ---------------------------------------------------------------------------

export interface UnitAlertRow {
  id: number;
  tipo: string;
  severidad: string;
  iniciadoAt: string;
  resueltoAt: string | null;
  payload: Record<string, unknown>;
  comentarioResolucion: string | null;
}

export interface UnitAlertsResponse {
  unitId: number;
  items: UnitAlertRow[];
  nextCursor: number | null;
}

export async function listUnitAlerts(
  unitId: number,
  params: {
    start?: string;
    end?: string;
    openOnly?: boolean;
    severity?: string;
    limit?: number;
    cursor?: number;
  } = {}
): Promise<UnitAlertsResponse> {
  const res = await api.get<UnitAlertsResponse>(
    `/fleet/units/${unitId}/alerts`,
    { params }
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// /fleet/units/{id}/playback (PR 6)
// ---------------------------------------------------------------------------

export interface PlaybackPoint {
  id: number;
  at: string;
  latitude: number;
  longitude: number;
  heading: number | null;
  speedKmh: number | null;
  estado: string;
  fuente: string;
  ignicion: boolean | null;
}

export interface PlaybackEvent {
  tipo: "hit" | "pausa_start" | "pausa_end" | "sesion_end" | string;
  at: string;
  refId: number | null;
  metadata: Record<string, unknown>;
}

export interface PlaybackResponse {
  unitId: number;
  start: string;
  end: string;
  requestedMaxPoints: number;
  sampled: boolean;
  sampleStride: number;
  rawCount: number;
  totalPoints: number;
  points: PlaybackPoint[];
  events: PlaybackEvent[];
  cachedAt: string | null;
}

export async function getUnitPlayback(
  unitId: number,
  params: {
    start: string;
    end: string;
    maxPoints?: number;
    fuentes?: string;
  }
): Promise<PlaybackResponse> {
  const query: Record<string, string | number> = {
    start: params.start,
    end: params.end,
  };
  if (params.maxPoints !== undefined) query.max_points = params.maxPoints;
  if (params.fuentes) query.fuentes = params.fuentes;
  const res = await api.get<PlaybackResponse>(
    `/fleet/units/${unitId}/playback`,
    { params: query }
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// /fleet/units/{id}/playback/export + /fleet/playback/exports/{id} (PR 7)
// ---------------------------------------------------------------------------

export type PlaybackExportFormat = "gpx" | "kml" | "csv" | "pdf";

export interface PlaybackExportRequest {
  start: string;
  end: string;
  formato: PlaybackExportFormat;
  maxPoints?: number;
  fuentes?: string;
}

export interface PlaybackExportStatus {
  reportId: number;
  estado: "pendiente" | "generando" | "listo" | "error";
  formato: PlaybackExportFormat | string;
  errorMensaje: string | null;
  solicitadoAt: string;
  terminadoAt: string | null;
  downloadUrl: string | null;
  expiresAt: string | null;
  bytesSize: number | null;
}

export async function requestPlaybackExport(
  unitId: number,
  body: PlaybackExportRequest
): Promise<PlaybackExportStatus> {
  const res = await api.post<PlaybackExportStatus>(
    `/fleet/units/${unitId}/playback/export`,
    body
  );
  return res.data;
}

export async function getPlaybackExportStatus(
  reportId: number
): Promise<PlaybackExportStatus> {
  const res = await api.get<PlaybackExportStatus>(
    `/fleet/playback/exports/${reportId}`
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// /fleet/heatmap (PR 8)
// ---------------------------------------------------------------------------

export type HeatmapGrid = "fine" | "medium" | "coarse";

export interface HeatmapCell {
  lat: number;
  lng: number;
  count: number;
}

export interface HeatmapResponse {
  start: string;
  end: string;
  gridSizeDeg: number;
  fuentes: string[] | null;
  unitId: number | null;
  rawCount: number;
  cells: HeatmapCell[];
}

export async function getFleetHeatmap(params: {
  start: string;
  end: string;
  grid?: HeatmapGrid;
  fuentes?: string;
  unitId?: number;
}): Promise<HeatmapResponse> {
  const query: Record<string, string | number> = {
    start: params.start,
    end: params.end,
  };
  if (params.grid) query.grid = params.grid;
  if (params.fuentes) query.fuentes = params.fuentes;
  if (params.unitId !== undefined) query.unit_id = params.unitId;
  const res = await api.get<HeatmapResponse>("/fleet/heatmap", { params: query });
  return res.data;
}

export async function listCommands(unitId: number): Promise<
  Array<{
    id: string;
    tipo: string;
    payload: Record<string, unknown>;
    creado_at: string;
    entregado_at: string | null;
    cancelado_at: string | null;
    logs: Array<{ estado: string; mensaje: string | null; at: string }>;
  }>
> {
  const res = await api.get(`/admin/edges/units/${unitId}/commands`);
  return res.data;
}

export async function issueCommand(
  unitId: number,
  body: { tipo: string; payload?: Record<string, unknown> }
): Promise<{ id: string; tipo: string; creado_at: string }> {
  const res = await api.post(`/admin/edges/units/${unitId}/commands`, body);
  return res.data;
}
